"use client";

import i18n from "@/lang/i18n";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  ButtonBase,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SendIcon from "@mui/icons-material/Send";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { usePortalStore } from "@/stores/portalStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDarkColors } from "@/hooks/useDarkColors";
import { useDiamondShortageModal } from "@/hooks/useDiamondShortageModal";
import { useTranslator } from "@/lang";
import {
  TEMP_TITLE_EXTRABOLD_XL,
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_REGULAR_M,
  TEMP_TEXT_REGULAR_XS,
  TEMP_TEXT_BOLD_XS,
  TEMP_TEXT_BOLD_XXS,
} from "@/constants/tempTypo";
import { TEMP_FG_ACCENT } from "@/constants/tempColors";
import { SAFE_AREA_TOP } from "@/contexts/SafeAreaContext";
import {
  getUserFeedDetail,
  createUserFeedComment,
  createUserFeedReply,
  deleteUserFeedComment,
  deleteUserFeedReply,
  type UserFeedCommentItem,
  type UserFeedAuthor,
  type ReactionType,
  type ReactionTarget,
  type UserFeedReactions,
} from "@/api/userFeedApi";
import { getAssetUrl } from "@/utils/assetUrl";
import { Icon } from "@/components/icons";
import { APP_MAX_WIDTH } from "@/components/PageContent/PageContent";
import { useStackNavigation } from "@/hooks/useStackNavigation";
import { renderFeedBody } from "../_components/renderFeedBody";
import { stripFeedActions } from "../_components/stripFeedActions";
import ReactionListSheet from "../_components/ReactionListSheet";
import FeedImage from "../_components/FeedImage";

interface Props {
  feedId: string;
  onBack: () => void;
  inStack?: boolean;
}

const REACTION_EMOJI: Record<ReactionType, string> = {
  HEART: '❤️',
  LAUGH: '😂',
  WOW: '😮',
  SAD: '😢',
  ANGRY: '😡',
  FIRE: '🔥',
  CLAP: '👏',
};

export default function MyFeedDetailContent({
  feedId,
  onBack,
  inStack = false,
}: Props) {
  const c = useDarkColors();
  const qc = useQueryClient();
  const { t, f } = useTranslator();
  const { showDiamondShortage } = useDiamondShortageModal();

  // visualViewport 추적 — 키보드/페이지 스크롤에 따라 컨테이너 사이즈 변경
  const [vv, setVv] = useState<{ height: number; offsetTop: number }>(() => ({
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
    offsetTop: 0,
  }));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => {
      const v = window.visualViewport;
      setVv({
        height: v ? v.height : window.innerHeight,
        offsetTop: v ? v.offsetTop : 0,
      });
    };
    update();
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const [replyTo, setReplyTo] = useState<{
    commentId: string;
    targetName: string;
    /** 답글 대상의 프로필 이미지 경로 — 헤더 아바타로 표시 */
    targetAvatarPath?: string | null;
  } | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openBottomSheet = usePortalStore((s) => s.openBottomSheet);
  const openReactionSheet = (targetType: ReactionTarget, targetId: string) => {
    openBottomSheet({
      content: (
        <ReactionListSheet
          feedId={feedId}
          targetType={targetType}
          targetId={targetId}
        />
      ),
      midHeightPercent: 50,
      maxHeightPercent: 90,
    });
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["user-feed-detail", feedId],
    queryFn: () => getUserFeedDetail(feedId),
    enabled: !!feedId,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    qc.invalidateQueries({ queryKey: ["notification-unread"] });
    qc.invalidateQueries({ queryKey: ["notification-groups"] });
  }, [feedId, qc]);

  const handleSubmit = async () => {
    if (!feedId || !text.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      if (replyTo) {
        await createUserFeedReply(feedId, replyTo.commentId, text.trim());
      } else {
        await createUserFeedComment(feedId, text.trim());
      }
      setText("");
      setReplyTo(null);
      refetch();
    } catch (err: any) {
      const code = err?.response?.data?.message;
      if (code === "INSUFFICIENT_BALANCE") {
        showDiamondShortage();
      } else {
        setError(t("my_feed.detail.send_failed"));
      }
    } finally {
      setSending(false);
    }
  };

  if (isLoading || !data) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: vv.offsetTop,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: APP_MAX_WIDTH,
          height: vv.height,
          bgcolor: c.bg,
          display: 'flex',
          justifyContent: 'center',
          pt: 8,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  const inner = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // visualViewport에 정확히 fit → 입력바가 키보드 위에 자연스럽게 sticky
        height: vv.height,
        position: 'fixed',
        top: vv.offsetTop,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: APP_MAX_WIDTH,
        bgcolor: c.bg,
        zIndex: 1,
      }}
    >
      {/* 헤더 - 상단 고정 */}
      <Box sx={{ flexShrink: 0, bgcolor: c.bg, pt: inStack ? 0 : SAFE_AREA_TOP }}>
        <Box sx={{ display: "flex", alignItems: "center", height: 56, px: "20px", gap: 1, borderBottom: `1px solid ${c.border}` }}>
          <ButtonBase onClick={onBack} aria-label={t("my_feed.aria.back")}>
            <ArrowBackIosNewIcon sx={{ fontSize: 18, color: c.fg }} />
          </ButtonBase>
          <Typography sx={{ ...TEMP_TITLE_EXTRABOLD_XL, color: c.fg, ml: 1 }}>
            {t("my_feed.detail.title")}
          </Typography>
        </Box>
      </Box>

      {/* 스크롤 영역 — 본문 + 댓글 트리 */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {/* 본문 */}
        <Box sx={{ px: "20px", py: "16px", borderBottom: `1px solid ${c.border}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {data.authorProfileImageUrl ? (
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: "50%", bgcolor: c.bgInset, overflow: "hidden",
                  backgroundImage: `url(${getAssetUrl(data.authorProfileImageUrl, 'large')})`,
                  backgroundSize: "cover", backgroundPosition: "center", flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: '50%', bgcolor: c.bgInset,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >
                <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fgLow }}>
                  {(data.authorUserName ?? t('my_feed.author.me')).slice(0, 1)}
                </Typography>
              </Box>
            )}
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>{data.authorUserName ?? t('my_feed.author.me')}</Typography>
          </Box>
          <Typography
            component="div"
            sx={{ ...TEMP_TEXT_REGULAR_M, color: c.fg, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {renderFeedBody(
              data.content,
              (data.mentionedCharacters ?? []).map((m) => ({
                characterId: m.characterId,
                name: pickName(m.name, t),
                nameVariants: collectNameVariants(m.name),
                profileImagePath: m.profileImagePath,
              })),
              c.primary,
            )}
          </Typography>
          {data.imagePath && (
            <FeedImage path={data.imagePath} variant="large" maxHeight={480} />
          )}
          {/* 피드 리액션 + 댓글 수 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
            <ReactionCluster
              reactions={data.reactions.feed}
              onClick={() => openReactionSheet('FEED', data.id)}
            />
            <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLower }}>
              {f(t('my_feed.detail.comment_count'), [data.commentCount])}
            </Typography>
          </Box>
        </Box>

        {/* 댓글 트리 */}
        <Box>
          {data.comments.length === 0 && (
            <Box sx={{ px: "20px", py: 4, textAlign: "center" }}>
              <Typography sx={{ ...TEMP_TEXT_REGULAR_M, color: c.fgLow }}>
                {t('my_feed.detail.muses_will_react_soon')}
              </Typography>
            </Box>
          )}
          {data.comments.map((cm) => (
            <CommentBlock
              key={cm.id}
              feedId={feedId}
              comment={cm}
              reactions={data.reactions}
              onReply={(name) =>
                setReplyTo({
                  commentId: cm.id,
                  targetName: name ?? authorDisplay(cm.author, t),
                  targetAvatarPath:
                    cm.author.authorType === 'CHARACTER'
                      ? cm.author.characterProfileImagePath
                      : cm.author.userProfileImageUrl,
                })
              }
              onReactionClick={(targetType, targetId) =>
                openReactionSheet(targetType, targetId)
              }
              onRefresh={() => refetch()}
            />
          ))}
        </Box>
      </Box>

      {/* 입력바 — viewport 컨테이너의 자연스러운 bottom (키보드가 컨테이너를 줄이므로 자동 위치 조정) */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: c.bg,
          borderTop: `1px solid ${c.border}`,
        }}
      >
        {replyTo && (
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              bgcolor: c.bgInset, px: 2, py: 0.75, borderBottom: `1px solid ${c.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {/* 답글 대상의 프로필 아바타 — 누구한테 답글 다는지 시각적으로 명확히 */}
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: c.bg,
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundImage: replyTo.targetAvatarPath
                    ? `url(${getAssetUrl(replyTo.targetAvatarPath, 'large')})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: `1px solid ${c.border}`,
                }}
              />
              <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLow, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f(t('my_feed.detail.reply_to'), [replyTo.targetName])}
              </Typography>
            </Box>
            <ButtonBase
              onClick={() => setReplyTo(null)}
              sx={{ ...TEMP_TEXT_BOLD_XS, color: c.primary, px: 1, flexShrink: 0 }}
            >
              {t('common.cancel')}
            </ButtonBase>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25 }}>
          <Box
            sx={{
              flex: 1,
              bgcolor: c.bgInset,
              borderRadius: 999,
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              placeholder={replyTo ? t('my_feed.detail.reply_placeholder') : t('my_feed.detail.comment_placeholder')}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 300))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: c.fg,
                fontSize: 15,
                lineHeight: 1.4,
                fontFamily: 'inherit',
              }}
            />
          </Box>
          <IconButton onClick={handleSubmit} disabled={sending || text.trim().length === 0}>
            {sending ? <CircularProgress size={18} /> : <SendIcon sx={{ color: c.primary }} />}
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Box
          sx={{
            position: "fixed", bottom: 96, left: 12, right: 12,
            bgcolor: "#e53935", color: "#fff", p: 1, borderRadius: 1, textAlign: "center",
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_REGULAR_M, color: "#fff" }}>{error}</Typography>
        </Box>
      )}

    </Box>
  );

  // inStack/page 모두 동일 — inner가 visualViewport에 fit하는 fixed 컨테이너이므로 wrapper 불필요
  return inner;
}

// ─────────────── 댓글 블록 ───────────────

function CommentBlock({
  feedId,
  comment,
  reactions,
  onReply,
  onReactionClick,
  onRefresh,
}: {
  feedId: string;
  comment: UserFeedCommentItem;
  reactions: UserFeedReactions;
  onReply: (name: string | null) => void;
  onReactionClick: (t: ReactionTarget, id: string) => void;
  onRefresh: () => void;
}) {
  const c = useDarkColors();
  const { t } = useTranslator();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const openConfirm = usePortalStore((s) => s.openConfirm);

  const handleDelete = () => {
    setMenuAnchor(null);
    openConfirm({
      title: t('my_feed.detail.delete_comment_title'),
      titleIcon: (
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#FFE5E5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DeleteOutlineIcon sx={{ fontSize: 26, color: '#e53935' }} />
        </Box>
      ),
      subtitle: t('my_feed.detail.delete_comment_subtitle'),
      cancelText: t('common.cancel'),
      confirmText: t('my_feed.common.delete'),
      onConfirm: async () => {
        try {
          await deleteUserFeedComment(feedId, comment.id);
          onRefresh();
        } catch {}
      },
    });
  };

  return (
    <Box sx={{ px: "20px", py: "14px", borderBottom: `1px solid ${c.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <AuthorAvatar author={comment.author} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AuthorHeader author={comment.author} />
          <Typography
            sx={{ ...TEMP_TEXT_REGULAR_M, color: c.fg, whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}
          >
            {comment.author.authorType === 'CHARACTER'
              ? stripFeedActions(comment.content)
              : comment.content}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            <ButtonBase
              onClick={() => onReply(authorDisplay(comment.author, t))}
              sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLow }}
            >
              {t('my_feed.detail.reply_label')}
            </ButtonBase>
            <ReactionCluster
              reactions={reactions.comments[comment.id] ?? {}}
              onClick={() => onReactionClick('COMMENT', comment.id)}
              small
            />
          </Box>
        </Box>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: c.fgLow }}>
          <MoreHorizIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={handleDelete} sx={{ color: '#e53935' }}>{t('my_feed.list.delete_menu')}</MenuItem>
        </Menu>
      </Box>

      {/* 대댓글 */}
      {comment.replies.length > 0 && (
        <Box sx={{ mt: 1, pl: '44px' }}>
          {comment.replies.map((r) => (
            <ReplyBlock
              key={r.id}
              feedId={feedId}
              commentId={comment.id}
              reply={r}
              reactions={reactions}
              onReactionClick={onReactionClick}
              onRefresh={onRefresh}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function ReplyBlock({
  feedId,
  commentId,
  reply,
  reactions,
  onReactionClick,
  onRefresh,
}: {
  feedId: string;
  commentId: string;
  reply: UserFeedCommentItem['replies'][0];
  reactions: UserFeedReactions;
  onReactionClick: (t: ReactionTarget, id: string) => void;
  onRefresh: () => void;
}) {
  const c = useDarkColors();
  const { t: tt } = useTranslator();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const openConfirm = usePortalStore((s) => s.openConfirm);
  const handleDelete = () => {
    setMenuAnchor(null);
    openConfirm({
      title: tt('my_feed.detail.delete_reply_title'),
      titleIcon: (
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#FFE5E5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DeleteOutlineIcon sx={{ fontSize: 26, color: '#e53935' }} />
        </Box>
      ),
      subtitle: tt('my_feed.detail.delete_reply_subtitle'),
      cancelText: tt('common.cancel'),
      confirmText: tt('my_feed.common.delete'),
      onConfirm: async () => {
        try {
          await deleteUserFeedReply(feedId, commentId, reply.id);
          onRefresh();
        } catch {}
      },
    });
  };
  return (
    <Box sx={{ py: 0.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <AuthorAvatar author={reply.author} small />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AuthorHeader author={reply.author} small />
        <Typography
          sx={{ ...TEMP_TEXT_REGULAR_M, color: c.fg, whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}
        >
          {reply.author.authorType === 'CHARACTER'
            ? stripFeedActions(reply.content)
            : reply.content}
        </Typography>
        <Box sx={{ mt: 0.25 }}>
          <ReactionCluster
            reactions={reactions.replies[reply.id] ?? {}}
            onClick={() => onReactionClick('REPLY', reply.id)}
            small
          />
        </Box>
      </Box>
      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: c.fgLow }}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleDelete} sx={{ color: '#e53935' }}>{tt('my_feed.list.delete_menu')}</MenuItem>
      </Menu>
    </Box>
  );
}

// ─────────────── 아바타 / 헤더 (이름 + 친밀도 뱃지) ───────────────

function AuthorAvatar({ author, small = false }: { author: UserFeedAuthor; small?: boolean }) {
  const c = useDarkColors();
  const { push } = useStackNavigation();
  const size = small ? 28 : 32;
  const url =
    author.authorType === 'CHARACTER' && author.characterProfileImagePath
      ? getAssetUrl(author.characterProfileImagePath, 'large')
      : getAssetUrl(author.userProfileImageUrl, 'large');
  if (!url && author.authorType === 'USER') return null;
  const isCharacter = author.authorType === 'CHARACTER' && !!author.characterId;
  return (
    <Box
      onClick={
        isCharacter
          ? () => push(`/profile-detail/${author.characterId}`)
          : undefined
      }
      sx={{
        width: size, height: size, borderRadius: '50%',
        bgcolor: c.bgInset, overflow: 'hidden', flexShrink: 0,
        backgroundImage: url ? `url(${url})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
        cursor: isCharacter ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    />
  );
}

function AuthorHeader({ author, small = false }: { author: UserFeedAuthor; small?: boolean }) {
  const c = useDarkColors();
  const { t } = useTranslator();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={{ ...(small ? TEMP_TEXT_BOLD_XS : TEMP_TEXT_BOLD_M), color: c.fg }}>
        {authorDisplay(author, t)}
      </Typography>
      {/* "뮤즈" 라벨 제거. 매칭된 캐릭터(intimacy != null)에만 친밀도 뱃지 표시. */}
      {author.authorType === 'CHARACTER' && typeof author.intimacy === 'number' && author.intimacy > 0 ? (
        <IntimacyBadge intimacy={author.intimacy} isGirlfriend={!!author.isGirlfriend} />
      ) : null}
    </Box>
  );
}

function IntimacyBadge({ intimacy, isGirlfriend = false }: { intimacy: number; isGirlfriend?: boolean }) {
  const c = useDarkColors();
  const showMax = (!isGirlfriend && intimacy >= 89) || intimacy >= 100;
  return (
    <Box
      sx={{
        display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'center',
        bgcolor: c.bgInset, borderRadius: '9999px', pl: '6px', pr: '8px', py: '2px', flexShrink: 0,
      }}
    >
      <Icon name="icHeartFilled" size={10} color={TEMP_FG_ACCENT} />
      <Typography sx={{ ...TEMP_TEXT_BOLD_XXS, color: c.fg, whiteSpace: 'nowrap' }}>
        {intimacy}°C{showMax ? ' MAX' : ''}
      </Typography>
    </Box>
  );
}

// ─────────────── 리액션 클러스터 + 시트 ───────────────

function ReactionCluster({
  reactions,
  onClick,
  small = false,
}: {
  reactions: Partial<Record<ReactionType, number>>;
  onClick: () => void;
  small?: boolean;
}) {
  const c = useDarkColors();
  const entries = useMemo(
    () =>
      (Object.entries(reactions) as [ReactionType, number][])
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4),
    [reactions],
  );
  const total = entries.reduce((s, [, n]) => s + n, 0);
  if (total === 0) return null;
  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: c.bgInset,
        borderRadius: 999,
        px: small ? 0.75 : 1,
        py: small ? '2px' : '4px',
      }}
    >
      {entries.map(([type]) => (
        <Box key={type} sx={{ fontSize: small ? 12 : 14, lineHeight: 1 }}>
          {REACTION_EMOJI[type]}
        </Box>
      ))}
      <Typography sx={{ ...TEMP_TEXT_BOLD_XXS, color: c.fg, ml: 0.25 }}>{total}</Typography>
    </ButtonBase>
  );
}

// ─────────────── helpers ───────────────

// t는 컴포넌트 트리 안에서만 호출 가능하므로 helper에 명시적으로 전달받는다.
function authorDisplay(
  author: UserFeedAuthor,
  t: (key: string) => string,
): string {
  if (author.authorType === 'USER')
    return author.userName ?? t('my_feed.author.me');
  return pickName(author.characterName, t);
}
function pickName(name: any, t: (key: string) => string): string {
  const muse = t('my_feed.author.muse');
  if (!name) return muse;
  if (typeof name === 'string') return name;
  if (typeof name === 'object')
    return name[i18n.language] ?? name.ko ?? name.en ?? Object.values(name)[0] ?? muse;
  return muse;
}

function collectNameVariants(name: any): string[] {
  if (!name) return [];
  if (typeof name === 'string') return [name];
  if (typeof name === 'object')
    return Object.values(name).filter((v): v is string => typeof v === 'string' && v.length > 0);
  return [];
}
